import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCheckoutCart } from '../contexts/CheckoutCartContext';
import { pushEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import { navigateToCheckoutOverlay } from '../utils/checkoutOverlayNavigation';
import './PlanChooser.css';

const SALES_PAGE_EXPERIMENT_PARAM_KEYS = [
  '$feature/sales_page_signup_framing_v4_variant',
  '$feature_flag_sales_page_signup_framing_v4_variant',
  '$feature/sales_page_signup_framing_v5_variant',
  '$feature_flag_sales_page_signup_framing_v5_variant',
  '$feature/google_search_signup_experience_v1_variant',
  '$feature_flag_google_search_signup_experience_v1_variant',
  '$feature/google_search_signup_experience_v2_variant',
  '$feature_flag_google_search_signup_experience_v2_variant',
  '$feature/google_search_auth_first_personalization_2026_08_variant',
  '$feature_flag_google_search_auth_first_personalization_2026_08_variant',
  'feature_flag',
  'experiment_name',
  'experiment_variant',
  'sales_page_signup_framing_v4_experiment_name',
  'sales_page_signup_framing_v5_experiment_name',
  'sales_page_signup_framing_variant',
  'sales_page_signup_framing_v4_variant',
  'sales_page_signup_framing_v4_route_variant',
  'sales_page_signup_framing_v5_variant',
  'sales_page_signup_framing_v5_route_variant',
  'google_search_signup_experience_v1_experiment_name',
  'google_search_signup_experience_v1_variant',
  'google_search_signup_experience_v2_experiment_name',
  'google_search_signup_experience_v2_variant',
  'google_search_auth_first_personalization_2026_08_experiment_name',
  'google_search_auth_first_personalization_2026_08_variant',
  'google_search_lp_job',
  'sales_page_form_variant',
  'sales_page_framing_variant',
  'sales_page_design_variant',
];

/**
 * PlanChooser - Full-page paywall blocker shown when users lack payment
 *
 * Used by ProtectedRoute when requirePayment=true and user is not paying.
 * This is a BLOCKING component (not a modal) - user cannot dismiss it.
 * They must click "Upgrade to Starter Plan" to proceed.
 *
 * Matches PaywallModal visual design but designed for route-level blocking.
 */
export default function PlanChooser({
  heading = 'Upgrade Required',
  body = 'This feature is not included in your current plan. Upgrade to unlock full access.',
  checkoutUrl = '/checkout/starter-plan',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { replaceCart, pending: cartPending } = useCheckoutCart();
  const [error, setError] = useState(null);
  const successUrl = useMemo(
    () => searchParams.get('successUrl') || `${location.pathname}${location.search}`,
    [location.pathname, location.search, searchParams],
  );
  const salesExperimentParams = useMemo(() => (
    SALES_PAGE_EXPERIMENT_PARAM_KEYS.reduce((params, key) => {
      const value = searchParams.get(key);
      if (value) {
        params.set(key, value);
      }
      return params;
    }, new URLSearchParams())
  ), [searchParams]);

  const buildCheckoutPath = () => {
    const params = new URLSearchParams(salesExperimentParams);
    params.set('successUrl', successUrl);
    return `/checkout/starter-plan?${params.toString()}`;
  };

  const handleUpgradeClick = async () => {
    setError(null);

    // Track CTA click
    pushEvent({
      event: ANALYTICS_EVENTS.AI_AGENT_PAYWALL_CTA_CLICKED?.name || 'paywall_cta_clicked',
      cta_type: 'upgrade_starter_plan',
      source: 'plan_chooser',
      checkoutUrl,
    });

    try {
      await replaceCart([
        {
          productSlug: 'starter-plan',
          planKey: 'default',
          quantity: 1,
        },
      ]);
      // Use the starter checkout page like the rest of the signup flow
      navigateToCheckoutOverlay(navigate, buildCheckoutPath(), location);
    } catch (err) {
      console.error('[PlanChooser] Failed to prefill checkout cart', err);
      setError('We could not prefill that plan. Opening checkout—please select a plan there.');
      navigateToCheckoutOverlay(navigate, buildCheckoutPath(), location);
    }
  };

  return (
    <div className="plan-chooser-blocker">
      <div className="plan-chooser-content">
        {/* Rocket icon - matches PaywallModal */}
        <div className="plan-chooser-icon">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
            <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
            <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
            <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
          </svg>
        </div>

        <h2 className="plan-chooser-title">{heading}</h2>
        <p className="plan-chooser-message">{body}</p>

        {/* Primary CTA - top placement for mobile */}
        <button
          className="plan-chooser-btn plan-chooser-btn-primary"
          onClick={handleUpgradeClick}
          disabled={cartPending}
          type="button"
        >
          {cartPending ? 'Loading...' : 'Upgrade to the Starter Plan'}
        </button>

        {/* Benefits list */}
        <div className="plan-chooser-benefits">
          <h3>Unlock the Starter Plan for:</h3>
          <ul>
            <li>Unlimited AI conversations</li>
            <li>Run backtests on your strategies</li>
            <li>Deploy bots to live trading</li>
            <li>Priority support</li>
            <li>And more</li>
          </ul>
        </div>

        {/* Secondary CTA - bottom placement */}
        <button
          className="plan-chooser-btn plan-chooser-btn-primary"
          onClick={handleUpgradeClick}
          disabled={cartPending}
          type="button"
        >
          {cartPending ? 'Loading...' : 'Get the Starter Plan'}
        </button>

        {error && <div className="plan-chooser-error">{error}</div>}
      </div>
    </div>
  );
}
